/* eslint-disable no-confusing-arrow */
/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

import { PageSizeOptions } from 'const';
import { ExpandButton } from 'ui/atoms';
import theme from 'ui/theme';
import MainFont from 'ui/typography/mainFont';

import DropDown from '../../atoms/DropDown';
import Icon from '../../atoms/Icon';

import {
  DropDownContainer,
  ItemWrapper,
  ListWrapper,
  PageSizeContainer,
  PaginationData,
  ResultsContainer,
  ShowMoreLess
} from './pagination-style';

const Pagination = ({
  totalPages,
  totalRecords,
  onPageChange,
  pageNo = 0,
  pageSize = 10,
  showMore
}) => {
  const [currentPage, setCurrentPage] = useState(pageNo + 1 || 1);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const { t } = useTranslation();
  const [showAllNumbers, setShowAllNumbers] = useState(false);

  const range = (start, end) =>
    Array(end - start + 1)
      .fill()
      .map((_, index) => start + index);

  const calculateAndSetStartIndex = (page, size, records) => {
    const possibleStartIndex = records === 0 ? 0 : (page - 1) * size + 1;
    setStartIndex(possibleStartIndex);
  };

  const calculateAndSetEndIndex = (page, size, records) => {
    const possibleEndIndex = page * size;
    const endIndexFinal =
      records > possibleEndIndex ? possibleEndIndex : records;
    setEndIndex(endIndexFinal);
  };

  const onPageClicked = (page, isShowMore) => {
    const currentPageClicked = Math.max(0, Math.min(page, totalPages));
    setCurrentPage(currentPageClicked);
    calculateAndSetStartIndex(page, pageSize, totalRecords);
    calculateAndSetEndIndex(page, pageSize, totalRecords);

    onPageChange(page, showMore ? isShowMore : pageSize);
  };

  const handleMoveLeft = evt => {
    if (evt) {
      evt.preventDefault();
    }
    onPageClicked(currentPage - 1, showMore);
    return true;
  };

  const handleMoveRight = evt => {
    if (evt) {
      evt.preventDefault();
    }
    onPageClicked(currentPage + 1);
    return false;
  };

  const onPageSizeChange = selected => {
    setCurrentPage(1);
    onPageChange(1, selected.value);
  };

  useEffect(() => {
    calculateAndSetStartIndex(currentPage, pageSize, totalRecords);
    calculateAndSetEndIndex(currentPage, pageSize, totalRecords);
  }, [totalPages, totalRecords, pageSize, currentPage]);

  useEffect(() => {
    setCurrentPage(pageNo + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo]);

  const showNumbers = () => {
    if (totalPages < 7) {
      return range(1, totalPages).map((page, index) => (
        <ItemWrapper key={index} selected={currentPage === page}>
          <div onClick={() => onPageClicked(page)}>
            <MainFont type="pageSize">{page}</MainFont>
          </div>
        </ItemWrapper>
      ));
    }
    return (
      <>
        {!showAllNumbers &&
          range(1, 3).map((page, index) => (
            <ItemWrapper key={index} selected={currentPage === page}>
              <div onClick={() => onPageClicked(page)}>
                <MainFont type="pageSize">{page}</MainFont>
              </div>
            </ItemWrapper>
          ))}

        {!showAllNumbers ? (
          <ItemWrapper>
            <div onClick={() => setShowAllNumbers(true)}>
              <MainFont type="pageSize">...</MainFont>
            </div>
          </ItemWrapper>
        ) : (
          range(1, totalPages).map((page, index) => (
            <ItemWrapper key={index} selected={currentPage === page}>
              <div onClick={() => onPageClicked(page)}>
                <MainFont type="pageSize">{page}</MainFont>
              </div>
            </ItemWrapper>
          ))
        )}
        {!showAllNumbers &&
          range(totalPages - 3, totalPages).map((page, index) => (
            <ItemWrapper key={index} selected={currentPage === page}>
              <div onClick={() => onPageClicked(page)}>
                <MainFont type="pageSize">{page}</MainFont>
              </div>
            </ItemWrapper>
          ))}
      </>
    );
  };

  return (
    <PaginationData>
      <ResultsContainer>
        <MainFont type="pageSize">
          {showMore
            ? `${t('results')} ${pageSize} of ${totalRecords}`
            : `${t('results')} ${startIndex} — ${endIndex} of ${totalRecords}`}
        </MainFont>
      </ResultsContainer>

      {showMore ? (
        <ShowMoreLess>
          {pageSize > 10 && (
            <ExpandButton handleOnClick={e => handleMoveLeft(e)} fixShowLess />
          )}
          {currentPage < totalPages && (
            <ExpandButton handleOnClick={e => handleMoveRight(e)} />
          )}
        </ShowMoreLess>
      ) : (
        <>
          <ListWrapper>
            <ItemWrapper>
              <div>
                <Icon
                  type="icon-chevron-up"
                  color={theme().colors.blue.one}
                  rotate={-90}
                  onClick={e => (currentPage === 1 ? null : handleMoveLeft(e))}
                  disabled={currentPage === 1}
                />
              </div>
            </ItemWrapper>
            {showNumbers()}
            <ItemWrapper>
              <div>
                <Icon
                  type="icon-chevron-up"
                  onClick={e =>
                    currentPage === totalPages ? null : handleMoveRight(e)
                  }
                  color={theme().colors.blue.one}
                  disabled={currentPage === totalPages}
                  rotate={90}
                />
              </div>
            </ItemWrapper>
          </ListWrapper>
          <PageSizeContainer>
            <MainFont type="showLabel">{t('show')}</MainFont>
            <DropDownContainer>
              <DropDown
                placeholder=""
                options={PageSizeOptions}
                defaultIndex={PageSizeOptions.findIndex(
                  opt => opt.value === pageSize
                ).toString()}
                onChange={onPageSizeChange}
                menuWidth={70}
                checkBoxMenu
                paginationMenu
                hideSelectedOptions
              />
            </DropDownContainer>
          </PageSizeContainer>
        </>
      )}
    </PaginationData>
  );
};

Pagination.propTypes = {
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  totalRecords: PropTypes.number,
  pageNo: PropTypes.number,
  pageSize: PropTypes.number,
  showMore: PropTypes.bool
};

export default Pagination;
